import React, { useState } from 'react';
import {
    Form,
    Input,
    Select,
    DatePicker,
    Button,
    Space,
    Row,
    Col,
    Alert,
    Divider,
    InputNumber,
    AutoComplete,
    Typography,
    Steps,
    Table,
    Card,
    message,
    Statistic,
    Descriptions
} from 'antd';
import { SearchOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api from '../api/client';

const { Text } = Typography;
const { Step } = Steps;
const { Option } = Select;

const SlaughterWasteForm = ({ onCancel, onSuccess, farms, users, apiUrl }) => {
    const [form] = Form.useForm();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedAnimal, setSelectedAnimal] = useState(null);
    const [animalOptions, setAnimalOptions] = useState([]);
    const [loadingAnimals, setLoadingAnimals] = useState(false);
    const [verificationResult, setVerificationResult] = useState(null);
    const [wasteItems, setWasteItems] = useState([
        { id: 1, name: 'Hooves', weight: 0, price: 0 },
        { id: 2, name: 'Head', weight: 0, price: 0 },
        { id: 3, name: 'Skin', weight: 0, price: 0 },
        { id: 4, name: 'Internal Organs', weight: 0, price: 0 },
    ]);
    const [totalWaste, setTotalWaste] = useState(0);
    const [calculatedSellableWeight, setCalculatedSellableWeight] = useState(0);

    // Tracks whether the user has manually typed into "Weight After" -
    // if they have, we stop auto-overwriting it with the calculated
    // suggestion whenever a waste weight changes. This used to be
    // detected via `form.isFieldTouched('weightAfter')`, but that flagged
    // the field as touched even when only *programmatic* setFieldsValue
    // calls had written to it, so the auto-recalculation kept getting
    // skipped and the field stayed stuck at whatever value was set when
    // the animal was first selected. Tracking it ourselves is explicit
    // and reliable.
    const [weightAfterEdited, setWeightAfterEdited] = useState(false);

    // NOTE: weightBefore and weightAfter live only in the Form (single source of
    // truth) - previously there was a duplicate `weightBefore` React state that
    // could drift out of sync with the form field. Removed.

    // Fetch animals for autocomplete
    const fetchAnimals = async (searchText) => {
        setLoadingAnimals(true);
        try {
            const response = await api.get(`/api/animals/search`, {
                params: {
                    search: searchText,
                    status: 'alive',
                    limit: 10,
                    farm: form.getFieldValue('farm')
                }
            });
            const animals = response.data.map((animal) => ({
                value: animal.id,
                label: `${animal.tagNumber} - ${animal.animalName || 'Unnamed'} (${animal.type_slug})`,
                ...animal
            }));
            setAnimalOptions(animals);
        } catch (error) {
            console.error('Error fetching animals:', error);
        } finally {
            setLoadingAnimals(false);
        }
    };

    // Handle animal selection
    const handleAnimalSelect = (value, option) => {
        setSelectedAnimal(option);
        const weight = option.weight || option.currentWeight || 0;
        form.setFieldsValue({
            tagNumber: option.tagNumber,
            animalType: option.type_slug,
            weightBefore: weight
        });
        // A new animal means we're starting fresh - any previous manual edit
        // to Weight After no longer applies, so force the suggestion to
        // overwrite it this one time.
        setWeightAfterEdited(false);
        recalculate(weight, wasteItems, true);
    };

    // Calculate total waste
    const calculateTotalWaste = (items) => items.reduce((sum, item) => sum + (item.weight || 0), 0);

    // Recalculate the *suggested* sellable weight from Weight Before - Total Waste.
    // This is a starting point only - the actual "Weight After" is what gets
    // physically weighed and entered/confirmed by the user, and is what we
    // verify against on submit.
    const recalculate = (before, items, forceOverwriteWeightAfter = false) => {
        const waste = calculateTotalWaste(items);
        const suggestedSellable = Math.max(0, (before || 0) - waste);
        setTotalWaste(waste);
        setCalculatedSellableWeight(suggestedSellable);

        // Only auto-fill weightAfter if the user hasn't already manually
        // edited it themselves - otherwise we'd clobber a real measurement
        // every time a waste field changes.
        const shouldOverwrite = forceOverwriteWeightAfter || !weightAfterEdited;
        form.setFieldsValue({
            totalWaste: waste,
            ...(shouldOverwrite ? { weightAfter: suggestedSellable } : {})
        });

        return suggestedSellable;
    };

    // Handle a manual edit to Weight After - once the user touches this
    // field directly, stop auto-overwriting it from the calculation.
    const handleWeightAfterChange = () => {
        setWeightAfterEdited(true);
    };

    // Handle weight change
    const handleWeightChange = (id, value) => {
        const updatedItems = wasteItems.map((item) => (item.id === id ? { ...item, weight: value || 0 } : item));
        setWasteItems(updatedItems);
        const before = form.getFieldValue('weightBefore') || 0;
        recalculate(before, updatedItems);
    };

    // Handle price change
    const handlePriceChange = (id, value) => {
        const updatedItems = wasteItems.map((item) => (item.id === id ? { ...item, price: value || 0 } : item));
        setWasteItems(updatedItems);
    };

    // Handle weight before change
    const handleWeightBeforeChange = (value) => {
        recalculate(value || 0, wasteItems);
    };

    // Waste table columns
    const wasteColumns = [
        {
            title: 'Item',
            dataIndex: 'name',
            key: 'name',
            width: 150
        },
        {
            title: 'Weight (kg)',
            dataIndex: 'weight',
            key: 'weight',
            render: (_, record) => (
                <InputNumber
                    style={{ width: '100%' }}
                    placeholder="0"
                    min={0}
                    step={0.5}
                    value={record.weight}
                    onChange={(value) => handleWeightChange(record.id, value)}
                />
            )
        },
        {
            title: 'Price/kg (KES)',
            dataIndex: 'price',
            key: 'price',
            render: (_, record) => (
                <InputNumber
                    style={{ width: '100%' }}
                    placeholder="0"
                    min={0}
                    step={0.5}
                    value={record.price}
                    onChange={(value) => handlePriceChange(record.id, value)}
                />
            )
        },
        {
            title: 'Total (KES)',
            key: 'total',
            render: (_, record) => <Text strong>{(record.weight * record.price).toFixed(2)}</Text>
        }
    ];

    // Next step
    const nextStep = () => {
        if (currentStep === 0) {
            form.validateFields(['farm', 'userId'])
                .then(() => setCurrentStep(currentStep + 1))
                .catch(() => {});
        } else if (currentStep === 1) {
            form.validateFields(['animalSearch', 'weightBefore'])
                .then(() => setCurrentStep(currentStep + 1))
                .catch(() => {});
        } else if (currentStep === 2) {
            const hasWeight = wasteItems.some((item) => item.weight > 0);
            if (!hasWeight) {
                message.warning('Please enter weight for at least one biological item');
                return;
            }
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        setCurrentStep(currentStep - 1);
    };

    // Submit form
    const handleSubmit = async () => {
        const totalWasteWeight = calculateTotalWaste(wasteItems);
        const values = form.getFieldsValue();
        const weightBeforeVal = Number(values.weightBefore) || 0;

        // weightAfter is the actual measured/confirmed sellable weight (editable
        // by the user, pre-filled with the calculated suggestion). This is what
        // we verify against weightBefore - totalWaste, so the check can now
        // actually catch a real mismatch instead of always passing.
        const weightAfterVal = Number(values.weightAfter) || 0;

        const expectedTotal = totalWasteWeight + weightAfterVal;
        const difference = Math.abs(weightBeforeVal - expectedTotal);


        if (difference > 0.01) {
            setVerificationResult({
                verified: false,
                weightBefore: weightBeforeVal,
                totalWaste: totalWasteWeight,
                sellable: weightAfterVal,
                totalWeight: expectedTotal,
                difference: difference
            });
            setCurrentStep(2);
            setTimeout(() => {
                document.querySelector('.verification-alert')?.scrollIntoView({ behavior: 'smooth' });
            }, 0);
            return;
        }

        setVerificationResult(null);
        setLoading(true);
        try {
            const selectedUser = users.find((u) => u.id === values.userId);

            const payload = {
                animalId: selectedAnimal?.value || values.animalId,
                tagNumber: selectedAnimal?.tagNumber || selectedAnimal?.label || values.tagNumber || '',
                animalType: selectedAnimal?.type_slug || selectedAnimal?.type || values.animalType || '',
                farm: selectedAnimal?.farm || values.farm || '',

                weightBefore: weightBeforeVal,
                weightAfter: weightAfterVal,
                sellableWeight: weightAfterVal,

                slaughterDate: values.slaughterDate ? values.slaughterDate.format('YYYY-MM-DD HH:mm:ss') : null,

                hoovesWeight: wasteItems.find((i) => i.name === 'Hooves')?.weight || 0,
                headWeight: wasteItems.find((i) => i.name === 'Head')?.weight || 0,
                skinWeight: wasteItems.find((i) => i.name === 'Skin')?.weight || 0,
                internalOrgansWeight: wasteItems.find((i) => i.name === 'Internal Organs')?.weight || 0,
                totalWasteWeight: Number(totalWasteWeight),

                userId: selectedUser?.id || values.userId || null,
                userName: selectedUser ? `${selectedUser.firstname} ${selectedUser.lastname || ''}`.trim() : '',

                remarks: values.remarks || ''
            };

            const response = await api.post(`/api/slaughter/waste`, payload);

            message.success('Slaughter biological record added successfully');
            onSuccess(response.data);
        } catch (error) {
            const errMsg = error.response?.data?.error || 'Failed to add records';
            message.error(errMsg);
            if (error.response?.data?.details) {
                setVerificationResult({
                    verified: false,
                    weightBefore: error.response.data.details.weightBefore,
                    totalWaste: error.response.data.details.totalWasteWeight,
                    sellable: error.response.data.details.sellableWeight,
                    totalWeight: error.response.data.details.totalWeight,
                    difference: error.response.data.details.difference
                });
                setCurrentStep(2);
            }
        } finally {
            setLoading(false);
        }
    };

    const sectionStyle = (step) => ({ display: currentStep === step ? 'block' : 'none' });

    const renderStepContent = () => (
        <>
            <div style={sectionStyle(0)}>
                <Alert
                    message="Farm & User Details"
                    description="Select the farm and the person recording this slaughter."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="farm" label="Farm" rules={[{ required: true, message: 'Please select farm' }]}>
                            <Select placeholder="Select farm">
                                {farms?.map((farm) => (
                                    <Option key={farm.id} value={farm.id}>
                                        {farm.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="userId" label="Recorded By" rules={[{ required: true, message: 'Please select user' }]}>
                            <Select placeholder="Select user">
                                {users.map((user) => (
                                    <Option key={user.id} value={user.id}>
                                        {user.firstname} {user.lastname}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
            </div>

            <div style={sectionStyle(1)}>
                <Alert
                    message="Animal & Weight Details"
                    description="Search and select the animal, then enter the weight before slaughter."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Form.Item
                    name="animalSearch"
                    label="Search Animal"
                    rules={[{ required: true, message: 'Please select an animal' }]}
                >
                    <AutoComplete
                        placeholder="Search by tag number or name"
                        style={{ width: '100%' }}
                        onSearch={fetchAnimals}
                        onSelect={handleAnimalSelect}
                        options={animalOptions}
                        loading={loadingAnimals}
                        filterOption={false}
                        notFoundContent="No animals found"
                    >
                        <Input suffix={<SearchOutlined />} />
                    </AutoComplete>
                </Form.Item>

                {selectedAnimal && (
                    <Alert
                        message="Animal Selected"
                        description={
                            <div>
                                <Text strong>Tag:</Text> {selectedAnimal.tagNumber}
                                <br />
                                <Text strong>Name:</Text> {selectedAnimal.animalName || 'Unnamed'}
                                <br />
                                <Text strong>Type:</Text> {selectedAnimal.type_slug}
                                <br />
                                <Text strong>Current Weight:</Text> {selectedAnimal.weight || selectedAnimal.currentWeight || 0} kg
                                <br />
                                <Text strong>Silo:</Text> {selectedAnimal.siloNumber || 'N/A'}
                            </div>
                        }
                        type="success"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Form.Item name="tagNumber" hidden>
                    <Input />
                </Form.Item>
                <Form.Item name="animalId" hidden>
                    <Input />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="weightBefore"
                            label="Weight Before (kg)"
                            rules={[{ required: true, message: 'Please enter weight before' }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder="Weight before slaughter"
                                min={0}
                                step={0.5}
                                onChange={handleWeightBeforeChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="weightAfter" label="Weight After (kg) - Actual Sellable Weight">
                            <InputNumber style={{ width: '100%' }} min={0} step={0.5} onChange={handleWeightAfterChange} />
                        </Form.Item>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Pre-filled from Weight Before − Total Waste. Adjust to the actual weighed
                            amount if it differs.
                        </Text>
                    </Col>
                </Row>

                <Form.Item
                    name="slaughterDate"
                    label="Slaughter Date"
                    rules={[{ required: true, message: 'Please select slaughter date' }]}
                >
                    <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>
            </div>

            <div style={sectionStyle(2)}>
                <Alert
                    message="Waste Items Breakdown"
                    description="Enter the weight and price for each waste item. Total waste is auto-calculated; adjust Weight After on the previous step if the actual weighed sellable amount differs."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                {verificationResult && (
                    <Alert
                        className="verification-alert"
                        message="Weight Verification Failed"
                        description={
                            <div>
                                <Text>Weight before: {verificationResult.weightBefore} kg</Text>
                                <br />
                                <Text>Total waste: {verificationResult.totalWaste} kg</Text>
                                <br />
                                <Text>Weight after (entered): {verificationResult.sellable} kg</Text>
                                <br />
                                <Text>Waste + Weight after: {verificationResult.totalWeight} kg</Text>
                                <br />
                                <Text type="danger">
                                    Difference from Weight Before: {verificationResult.difference.toFixed
                                        ? verificationResult.difference.toFixed(2)
                                        : verificationResult.difference}{' '}
                                    kg
                                </Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Go back to the previous step and correct Weight After, or adjust the
                                    waste item weights, so they add up to Weight Before.
                                </Text>
                            </div>
                        }
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
                <Table
                    columns={wasteColumns}
                    dataSource={wasteItems}
                    rowKey="id"
                    pagination={false}
                    size="middle"
                    bordered
                    footer={() => (
                        <Row gutter={16}>
                            <Col span={12}>
                                <Statistic title="Total Waste" value={totalWaste} suffix="kg" />
                            </Col>
                            <Col span={12}>
                                <Statistic
                                    title="Suggested Weight After"
                                    value={calculatedSellableWeight}
                                    suffix="kg"
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Col>
                        </Row>
                    )}
                />
                <Form.Item name="remarks" label="Remarks" style={{ marginTop: 16 }}>
                    <Input.TextArea placeholder="Additional notes" rows={3} />
                </Form.Item>
            </div>

            <div style={sectionStyle(3)}>
                <Alert
                    message="Review & Confirm"
                    description="Please review all details before submitting."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Card title="Summary">
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label="Farm">
                            {farms.find((f) => f.id === form.getFieldValue('farm'))?.name}
                        </Descriptions.Item>
                        <Descriptions.Item label="Recorded By">
                            {(() => {
                                const u = users.find((usr) => usr.id === form.getFieldValue('userId'));
                                return u ? `${u.firstname} ${u.lastname || ''}`.trim() : 'N/A';
                            })()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Animal">
                            {selectedAnimal?.tagNumber} - {selectedAnimal?.animalName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Weight Before">{form.getFieldValue('weightBefore')} kg</Descriptions.Item>
                        <Descriptions.Item label="Weight After">{form.getFieldValue('weightAfter')} kg</Descriptions.Item>
                        <Descriptions.Item label="Total Waste">{totalWaste} kg</Descriptions.Item>
                        <Descriptions.Item label="Slaughter Date">
                            {form.getFieldValue('slaughterDate')?.format('DD/MM/YYYY HH:mm')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Remarks">{form.getFieldValue('remarks') || 'N/A'}</Descriptions.Item>
                    </Descriptions>
                    <Divider>Waste Items</Divider>
                    <Table
                        columns={[
                            { title: 'Item', dataIndex: 'name' },
                            { title: 'Weight (kg)', dataIndex: 'weight' },
                            { title: 'Price/kg (KES)', dataIndex: 'price' },
                            {
                                title: 'Total (KES)',
                                render: (_, record) => (record.weight * record.price).toFixed(2)
                            }
                        ]}
                        dataSource={wasteItems.filter((i) => i.weight > 0)}
                        rowKey="id"
                        pagination={false}
                        size="small"
                    />
                </Card>
            </div>
        </>
    );

    return (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Steps current={currentStep} style={{ marginBottom: 24 }}>
                <Step title="Farm & User" />
                <Step title="Animal & Weight" />
                <Step title="Biological Items" />
                <Step title="Review" />
            </Steps>

            {renderStepContent()}

            <Divider />

            <Form.Item>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Space>
                        {currentStep > 0 && <Button onClick={prevStep}>Previous</Button>}
                        {currentStep < 3 && (
                            <Button type="primary" onClick={nextStep}>
                                Next
                            </Button>
                        )}
                        {currentStep === 3 && (
                            <Button type="primary" htmlType="submit" loading={loading}>
                                <CheckCircleOutlined /> Submit
                            </Button>
                        )}
                    </Space>
                </Space>
            </Form.Item>
        </Form>
    );
};

export default SlaughterWasteForm;
